import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FaBuilding, FaCamera, FaTrashAlt, FaUpload } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { DEFAULT_COMPANY_INFO, fetchCompanyInfo, resolveCompanyLogoUrl } from '../../../utils/companyInfo';

const emptyErrors = {
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_logo_path: '',
    general: ''
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const CompanyInfoSettings = () => {
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || '';
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({ ...DEFAULT_COMPANY_INFO });
    const [errors, setErrors] = useState(emptyErrors);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [removingLogo, setRemovingLogo] = useState(false);
    const [selectedLogoFile, setSelectedLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        const loadCompanyInfo = async () => {
            setLoading(true);
            const data = await fetchCompanyInfo(authToken);
            setFormData(data);
            setLoading(false);
        };

        loadCompanyInfo();
    }, [authToken]);

    const clearMessages = () => {
        setErrors(emptyErrors);
        setSuccessMessage('');
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        clearMessages();
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const nextErrors = { ...emptyErrors };

        if (!formData.company_name.trim()) {
            nextErrors.company_name = 'Company name is required';
        }
        if (!formData.company_address.trim()) {
            nextErrors.company_address = 'Company address is required';
        }
        if (!formData.company_phone.trim()) {
            nextErrors.company_phone = 'Company phone is required';
        }
        if (!formData.company_email.trim()) {
            nextErrors.company_email = 'Company email is required';
        } else if (!isValidEmail(formData.company_email.trim())) {
            nextErrors.company_email = 'Enter a valid email address';
        }

        setErrors(nextErrors);
        return !Object.values(nextErrors).some(Boolean);
    };

    const handleSave = async () => {
        clearMessages();
        if (!validateForm()) {
            return;
        }

        setSaving(true);
        try {
            const response = await axios.put(
                `${rootUrl}/system/company-info`,
                {
                    ...formData,
                    edit_by: user?.username || 'system'
                },
                {
                    headers: { Authorization: `Bearer ${authToken}` }
                }
            );

            setFormData(response.data?.data || formData);
            setSuccessMessage('Company information updated successfully.');
        } catch (error) {
            if (error.response?.data?.errors) {
                setErrors((prev) => ({ ...prev, ...error.response.data.errors }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    general: error.response?.data?.message || 'Failed to update company information.'
                }));
            }
        } finally {
            setSaving(false);
        }
    };

    const handleFileChange = (event) => {
        clearMessages();
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setErrors((prev) => ({
                ...prev,
                company_logo_path: 'Invalid file type. Upload a JPEG, PNG, GIF, or WebP image.'
            }));
            event.target.value = '';
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setErrors((prev) => ({
                ...prev,
                company_logo_path: 'Logo file must be smaller than 5MB.'
            }));
            event.target.value = '';
            return;
        }

        setSelectedLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const resetSelectedLogo = () => {
        if (logoPreview) {
            URL.revokeObjectURL(logoPreview);
        }
        setSelectedLogoFile(null);
        setLogoPreview('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleUploadLogo = async () => {
        if (!selectedLogoFile) {
            setErrors((prev) => ({ ...prev, company_logo_path: 'Choose a logo before uploading.' }));
            return;
        }

        clearMessages();
        setUploading(true);
        try {
            const payload = new FormData();
            payload.append('companyLogo', selectedLogoFile);
            payload.append('edit_by', user?.username || 'system');

            const response = await axios.post(`${rootUrl}/system/company-info/logo`, payload, {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                }
            });

            setFormData((prev) => ({
                ...prev,
                company_logo_path: response.data?.data?.company_logo_path || ''
            }));
            resetSelectedLogo();
            setSuccessMessage('Company logo uploaded successfully.');
        } catch (error) {
            setErrors((prev) => ({
                ...prev,
                company_logo_path: error.response?.data?.errors?.company_logo_path || error.response?.data?.message || 'Failed to upload company logo.'
            }));
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveLogo = async () => {
        clearMessages();

        if (selectedLogoFile) {
            resetSelectedLogo();
            return;
        }

        if (!formData.company_logo_path) {
            return;
        }

        setRemovingLogo(true);
        try {
            const response = await axios.delete(`${rootUrl}/system/company-info/logo`, {
                headers: { Authorization: `Bearer ${authToken}` },
                data: { edit_by: user?.username || 'system' }
            });

            setFormData((prev) => ({
                ...prev,
                company_logo_path: response.data?.data?.company_logo_path || ''
            }));
            setSuccessMessage('Company logo removed successfully.');
        } catch (error) {
            setErrors((prev) => ({
                ...prev,
                company_logo_path: error.response?.data?.message || 'Failed to remove company logo.'
            }));
        } finally {
            setRemovingLogo(false);
        }
    };

    const renderedLogoUrl = logoPreview || resolveCompanyLogoUrl(formData);

    if (loading) {
        return <div className="text-sm text-slate-600">Loading company information...</div>;
    }

    return (
        <div className="w-full max-w-5xl">
            <div className="rounded-xl border border-[#E7D6CA] bg-white shadow-sm">
                <div className="border-b border-[#EFE3DB] px-6 py-5">
                    <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#FFF1EB] text-[#FF5733]">
                            <FaBuilding className="text-lg" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-[#2a2e30]">Company Information</h2>
                            <p className="text-sm text-slate-500">Manage the company details used across invoices and print documents.</p>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-6">
                    {errors.general && (
                        <div className="mb-4 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {errors.general}
                        </div>
                    )}
                    {successMessage && (
                        <div className="mb-4 rounded-sm border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                            {successMessage}
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_0.8fr]">
                        <div className="space-y-5">
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                                <div className="md:col-span-2">
                                    <label htmlFor="company_name" className="mb-1 block text-sm font-semibold text-slate-700">
                                        Company Name
                                    </label>
                                    <input
                                        id="company_name"
                                        name="company_name"
                                        type="text"
                                        value={formData.company_name}
                                        onChange={handleInputChange}
                                        className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                                    />
                                    {errors.company_name && <p className="mt-1 text-xs text-red-500">{errors.company_name}</p>}
                                </div>

                                <div className="md:col-span-2">
                                    <label htmlFor="company_address" className="mb-1 block text-sm font-semibold text-slate-700">
                                        Company Address
                                    </label>
                                    <textarea
                                        id="company_address"
                                        name="company_address"
                                        value={formData.company_address}
                                        onChange={handleInputChange}
                                        rows="4"
                                        className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                                    />
                                    {errors.company_address && <p className="mt-1 text-xs text-red-500">{errors.company_address}</p>}
                                </div>

                                <div>
                                    <label htmlFor="company_phone" className="mb-1 block text-sm font-semibold text-slate-700">
                                        Company Phone
                                    </label>
                                    <input
                                        id="company_phone"
                                        name="company_phone"
                                        type="text"
                                        value={formData.company_phone}
                                        onChange={handleInputChange}
                                        className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                                    />
                                    {errors.company_phone && <p className="mt-1 text-xs text-red-500">{errors.company_phone}</p>}
                                </div>

                                <div>
                                    <label htmlFor="company_email" className="mb-1 block text-sm font-semibold text-slate-700">
                                        Company Email
                                    </label>
                                    <input
                                        id="company_email"
                                        name="company_email"
                                        type="email"
                                        value={formData.company_email}
                                        onChange={handleInputChange}
                                        className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                                    />
                                    {errors.company_email && <p className="mt-1 text-xs text-red-500">{errors.company_email}</p>}
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="rounded-sm bg-[#FF5733] px-4 py-2 text-sm font-semibold text-white shadow-none transition hover:bg-[#e34c28] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {saving ? 'Saving...' : 'Save Company Info'}
                                </button>
                            </div>
                        </div>

                        <div className="rounded-xl border border-[#EFE3DB] bg-[#FFFaf7] p-5">
                            <p className="mb-3 text-sm font-semibold text-slate-700">Company Logo</p>
                            <div className="flex flex-col items-center">
                                <div className="flex h-48 w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-[#D7C3B5] bg-white p-4">
                                    <img
                                        src={renderedLogoUrl}
                                        alt="Company logo preview"
                                        className="max-h-full max-w-full object-contain"
                                        crossOrigin="anonymous"
                                    />
                                </div>

                                <p className="mt-3 text-center text-xs text-slate-500">
                                    Supported formats: JPEG, PNG, GIF, WebP. Max size: 5MB.
                                </p>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="company-logo-input"
                                />

                                <div className="mt-4 flex flex-wrap justify-center gap-2">
                                    <label
                                        htmlFor="company-logo-input"
                                        className="inline-flex cursor-pointer items-center gap-2 rounded-sm bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                                    >
                                        <FaCamera />
                                        {selectedLogoFile || formData.company_logo_path ? 'Replace Logo' : 'Choose Logo'}
                                    </label>

                                    {selectedLogoFile && (
                                        <button
                                            type="button"
                                            onClick={handleUploadLogo}
                                            disabled={uploading}
                                            className="inline-flex items-center gap-2 rounded-sm bg-[#FF5733] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#e34c28] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <FaUpload />
                                            {uploading ? 'Uploading...' : 'Upload Logo'}
                                        </button>
                                    )}

                                    {(selectedLogoFile || formData.company_logo_path) && (
                                        <button
                                            type="button"
                                            onClick={handleRemoveLogo}
                                            disabled={removingLogo}
                                            className="inline-flex items-center gap-2 rounded-sm bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <FaTrashAlt />
                                            {selectedLogoFile ? 'Cancel Selection' : (removingLogo ? 'Removing...' : 'Remove Logo')}
                                        </button>
                                    )}
                                </div>

                                {selectedLogoFile && (
                                    <p className="mt-2 text-xs text-slate-500">{selectedLogoFile.name}</p>
                                )}
                                {errors.company_logo_path && (
                                    <p className="mt-2 text-center text-xs text-red-500">{errors.company_logo_path}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanyInfoSettings;
